import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import PageHeader from "../../../components/PageHeader";
import Input from "../../../components/ui/Input";
import Icon from "../../../components/AppIcon";
import FakeAppChrome from "../FakeAppChrome";
import TourNeoAiDock from "../TourNeoAiDock";
import {
  TOUR_WRITE_CONTRACT_BLOCKS,
  TOUR_WRITE_CONTRACT_MESSAGES,
  TOUR_WRITE_CONTRACT_STATUS_LINES,
  TOUR_WRITE_CONTRACT_TITLE,
} from "../tourData";

const INITIAL_MSG = TOUR_WRITE_CONTRACT_MESSAGES[0];
const USER_MSG = TOUR_WRITE_CONTRACT_MESSAGES[1];
const SUCCESS_MSG = TOUR_WRITE_CONTRACT_MESSAGES[2];

const WriteContractScene = () => {
  const [messages, setMessages] = useState([INITIAL_MSG]);
  const [loading, setLoading] = useState(false);
  const [composerText, setComposerText] = useState("");
  const [visibleBlocks, setVisibleBlocks] = useState(0);
  const [documentTitle, setDocumentTitle] = useState("");

  useEffect(() => {
    const timers = [];

    timers.push(
      window.setTimeout(() => {
        setComposerText("");
        let i = 0;
        const full = USER_MSG.text;
        const typeId = window.setInterval(() => {
          i += 4;
          setComposerText(full.slice(0, i));
          if (i >= full.length) window.clearInterval(typeId);
        }, 14);
        timers.push(typeId);
      }, 450)
    );

    timers.push(
      window.setTimeout(() => {
        setComposerText("");
        setMessages([INITIAL_MSG, USER_MSG]);
        setLoading(true);
      }, 2800)
    );

    timers.push(
      window.setTimeout(() => {
        setLoading(false);
        setMessages([INITIAL_MSG, USER_MSG, SUCCESS_MSG]);
        setDocumentTitle(TOUR_WRITE_CONTRACT_TITLE);
        let n = 0;
        const revealId = window.setInterval(() => {
          n += 1;
          setVisibleBlocks(n);
          if (n >= TOUR_WRITE_CONTRACT_BLOCKS.length) {
            window.clearInterval(revealId);
          }
        }, 220);
        timers.push(revealId);
      }, 5200)
    );

    return () => {
      timers.forEach((id) => {
        window.clearTimeout(id);
        window.clearInterval(id);
      });
    };
  }, []);

  return (
    <FakeAppChrome activeNavId="write-contract">
      <div className="relative flex min-h-full flex-col p-4 sm:p-6">
        <PageHeader
          title="Elaborar Documento"
          subtitle="Edite o documento com o NeoAi. Após gerar o PDF, posicione os campos de assinatura no painel de documentos."
          icon="PenLine"
          className="max-sm:mb-3 mb-4"
        />

        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card/95 px-3 py-2.5 shadow-sm sm:mb-4">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1.5 text-xs font-medium text-muted-foreground">
            <Icon name="Coins" size={14} />
            120 Neo Coins
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground">
            <Icon name="Save" size={14} />
            Salvar
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground">
            <Icon name="Eye" size={14} />
            Prévia
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2.5 py-1.5 text-xs font-semibold text-primary">
            <Icon name="FileText" size={14} />
            Gerar PDF
          </span>
        </div>

        <div className="mb-3 rounded-lg border border-border bg-card p-3 sm:mb-4 sm:p-4">
          <Input
            label="Título do documento"
            value={documentTitle}
            placeholder="Digite o título do documento"
            readOnly
            tabIndex={-1}
          />
        </div>

        <div className="relative min-h-[22rem] flex-1 rounded-lg border border-border bg-card shadow-soft-sm">
          <div className="min-h-[22rem] space-y-3 p-4 sm:p-6 lg:pr-[min(24rem,42%)]">
            {visibleBlocks === 0 ? (
              <p className="text-sm text-muted-foreground">
                {loading
                  ? "A NeoAi está elaborando o documento…"
                  : "O editor está vazio — a NeoAi vai gerar o texto a partir das suas instruções."}
              </p>
            ) : (
              <AnimatePresence initial={false}>
                {TOUR_WRITE_CONTRACT_BLOCKS.slice(0, visibleBlocks).map(
                  (block, index) => (
                    <motion.div
                      key={`${block.type}-${index}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.28 }}
                    >
                      {block.type === "heading" ? (
                        <h2 className="pt-1 text-base font-bold tracking-tight text-foreground sm:text-lg">
                          {block.text}
                        </h2>
                      ) : (
                        <p className="text-sm leading-relaxed text-foreground sm:text-[0.95rem]">
                          {block.text}
                        </p>
                      )}
                    </motion.div>
                  )
                )}
              </AnimatePresence>
            )}
          </div>

          <div className="pointer-events-none absolute bottom-4 right-4 z-[2] hidden lg:block">
            <div className="pointer-events-auto">
              <TourNeoAiDock
                messages={messages}
                loading={loading}
                composerText={composerText}
                placeholder="Ex.: Elabore um contrato de prestação de serviços…"
                loadingVariant="magic"
                statusLines={TOUR_WRITE_CONTRACT_STATUS_LINES}
              />
            </div>
          </div>
        </div>

        <div className="mt-4 lg:hidden">
          <TourNeoAiDock
            messages={messages}
            loading={loading}
            composerText={composerText}
            placeholder="Ex.: Elabore um contrato de prestação de serviços…"
            loadingVariant="magic"
            statusLines={TOUR_WRITE_CONTRACT_STATUS_LINES}
          />
        </div>
      </div>
    </FakeAppChrome>
  );
};

export default WriteContractScene;
